import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse } from 'axios';
import JwtService from '@/services/JwtService';
import { useAuthStore } from '@/stores/auth';
import Swal from 'sweetalert2';
import router from '@/router';

/**
 * @description standalone axios instance, independent of the Vue app instance
 * so it survives Vite HMR re-executing this module without needing re-init
 */
const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APP_API_URL,
});

/**
 * @description guards against showing multiple "session expired" alerts.
 * Stays true for the whole invalid-session episode (not just while the
 * alert is on screen) since logout/redirect is async and further requests
 * can still 401 with the stale token while it's in flight. Only cleared
 * once the user successfully authenticates again (see auth store setAuth).
 */
let isSessionExpiredAlertOpen = false;

axiosInstance.interceptors.request.use((config) => {
  // Set Authorization header
  config.headers.Authorization = `Bearer ${JwtService.getToken()}`;
  // Set default Accept header
  config.headers.Accept = 'application/json';

  // Set user Timezone
  config.headers.Timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  config.headers['X-Route'] = router.currentRoute.value.fullPath;

  return config;
});

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Ignore canceled requests
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    const authStore = useAuthStore();

    if (error.response) {
      const status = error.response.status;

      // Logout requests are allowed to fail silently (e.g. the token was
      // already invalid) — the auth store already handles this itself.
      const requestUrl = error.config?.url || '';
      const isLogoutRequest = requestUrl.includes('/logout');
      if (isLogoutRequest) {
        return Promise.reject(error);
      }

      if (status === 401) {
        if (!isSessionExpiredAlertOpen) {
          isSessionExpiredAlertOpen = true;
          Swal.fire({
            title: 'نشست شما منقضی شده است',
            text: 'برای ادامه، لطفاً دوباره وارد شوید.',
            icon: 'warning',
            buttonsStyling: false,
            confirmButtonText: 'ورود مجدد',
            heightAuto: false,
            allowOutsideClick: false,
            allowEscapeKey: false,
            customClass: {
              confirmButton: 'btn btn-primary',
            },
          }).then(() => {
            authStore.logout();
          });
        }
      } else if (status === 403) {
        // Permission-denied for one specific resource — not a session problem,
        // so no alert and no logout (403 used to be lumped in with 401 here,
        // which force-logged-out the whole session just because one widget's
        // permission was missing). Left silent for the caller: most dashboard
        // widgets check `isForbiddenError()` (utils/helpers/http.ts) and hide
        // themselves instead of showing an error state.
      } else if (status === 503) {
        // An upstream provider the backend depends on (e.g. Chatwoot) is
        // down — this backend's GuardsChatwootOutage turns that into a
        // clean 503 specifically so callers can react quietly instead of
        // with a generic error dialog. Left silent here too: callers check
        // `isServiceUnavailableError()` (utils/helpers/http.ts) and show
        // their own inline "offline" state (e.g. the chat modal's badge).
      } else if (status !== 422) {
        Swal.fire({
          text: (error.response.data as any)?.message || 'خطایی در سرور رخ داده است!',
          icon: 'error',
          buttonsStyling: false,
          confirmButtonText: 'OK',
          heightAuto: false,
          customClass: {
            confirmButton: 'btn btn-primary',
          },
        });
      }
    } else if (error.request) {
      // The request was made but no response was received
      Swal.fire({
        title: 'عدم ارتباط با سرور',
        text: 'اتصال به سرور برقرار نشد. لطفاً اینترنت خود را بررسی کنید و دوباره تلاش کنید.',
        icon: 'error',
        buttonsStyling: false,
        confirmButtonText: 'باشه',
        heightAuto: false,
        customClass: {
          confirmButton: 'btn btn-primary',
        },
      });
    }
    return Promise.reject(error);
  },
);

/**
 * @description service to call HTTP request via Axios
 */
class ApiService {
  /**
   * @description clears the session-expired alert guard, so a future 401/403
   * can show the alert again. Called by the auth store after a successful
   * re-authentication (see isSessionExpiredAlertOpen above).
   */
  public static resetSessionExpiredGuard(): void {
    isSessionExpiredAlertOpen = false;
  }

  /**
   * @description true unless value is undefined, null, or an empty string
   */
  private static isNotEmpty(value: any): boolean {
    return value !== undefined && value !== null && value !== '';
  }

  /**
   * @description recursively strips empty values (undefined/null/'', and
   * empty arrays/objects after their own contents are cleaned) from request
   * params, so query strings don't carry unset filters
   */
  private static cleanParams(obj: any): any {
    if (Array.isArray(obj)) {
      const cleanedArray = obj
        .map(ApiService.cleanParams)
        .filter((item) => ApiService.isNotEmpty(item));

      return cleanedArray.length ? cleanedArray : undefined;
    }

    if (typeof obj === 'object' && obj !== null) {
      const cleanedObj = Object.entries(obj)
        .map(([key, value]) => [key, ApiService.cleanParams(value)])
        .filter(([_, value]) => ApiService.isNotEmpty(value));

      return cleanedObj.length ? Object.fromEntries(cleanedObj) : undefined;
    }

    return ApiService.isNotEmpty(obj) ? obj : undefined;
  }

  /**
   * @description Generic request method
   * @returns Promise<AxiosResponse>
   * @param config
   */
  public static request<T = any>(
    config: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    config.params = ApiService.cleanParams(config.params);

    return axiosInstance.request(config);
  }
}

export default ApiService;
